/**
 * The oval guide: tells the page when a face sits inside the oval so the capture can start by itself.
 *
 * It uses the browser's own face detector when there is one (`FaceDetector`, Chrome and Android). Where there is
 * none, it falls back to a plain check on a small copy of the middle of the picture: the oval area must be well lit
 * and show some detail for about one second. The fallback only decides when to start; the server's quality gate
 * (E3) is what accepts or refuses the pictures. The small copy is drawn on a 32 x 40 canvas and thrown away.
 */

export const OVAL = { widthRatio: 0.5, heightRatio: 0.7 } as const

interface BrowserFaceDetector {
  detect(source: HTMLVideoElement): Promise<Array<{ boundingBox: DOMRectReadOnly }>>
}

/** True when the box of a face sits mostly inside the oval area (centred, not tiny). */
export function boxInOval(box: { x: number; y: number; width: number; height: number }, w: number, h: number) {
  const cx = box.x + box.width / 2
  const cy = box.y + box.height / 2
  const nx = (cx - w / 2) / ((w * OVAL.widthRatio) / 2)
  const ny = (cy - h / 2) / ((h * OVAL.heightRatio) / 2)
  const bigEnough = box.width >= w * 0.25 && box.height >= h * 0.3
  return nx * nx + ny * ny <= 1 && bigEnough
}

/** Mean and spread of the luminance of 8-bit RGBA pixels. */
export function lumaStats(data: ArrayLike<number>): { mean: number; spread: number } {
  const n = Math.floor(data.length / 4)
  if (n === 0) return { mean: 0, spread: 0 }
  let sum = 0
  let sum2 = 0
  for (let i = 0; i < n; i++) {
    const y = 0.299 * data[i * 4]! + 0.587 * data[i * 4 + 1]! + 0.114 * data[i * 4 + 2]!
    sum += y
    sum2 += y * y
  }
  const mean = sum / n
  return { mean, spread: Math.sqrt(Math.max(0, sum2 / n - mean * mean)) }
}

const HOLD_MS = 1000

export function createOvalChecker(video: HTMLVideoElement) {
  const Detector = (globalThis as { FaceDetector?: new (o?: object) => BrowserFaceDetector }).FaceDetector
  let detector: BrowserFaceDetector | null = null
  try {
    detector = Detector ? new Detector({ fastMode: true, maxDetectedFaces: 2 }) : null
  } catch {
    detector = null
  }
  let nativeResult = false
  let nativeBusy = false
  let okSince: number | null = null
  const canvas = document.createElement('canvas')
  canvas.width = 32
  canvas.height = 40

  const native = () => {
    if (!nativeBusy && detector) {
      nativeBusy = true
      detector
        .detect(video)
        .then((faces) => {
          nativeResult =
            faces.length === 1 && boxInOval(faces[0]!.boundingBox, video.videoWidth || 640, video.videoHeight || 480)
        })
        .catch(() => {
          detector = null
        })
        .finally(() => {
          nativeBusy = false
        })
    }
    return nativeResult
  }

  const fallback = () => {
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx || !video.videoWidth) return false
    const w = video.videoWidth
    const h = video.videoHeight
    const sw = w * OVAL.widthRatio
    const sh = h * OVAL.heightRatio
    ctx.drawImage(video, (w - sw) / 2, (h - sh) / 2, sw, sh, 0, 0, 32, 40)
    const { mean, spread } = lumaStats(ctx.getImageData(0, 0, 32, 40).data)
    ctx.clearRect(0, 0, 32, 40)
    const ok = mean > 50 && mean < 215 && spread > 18
    const now = Date.now()
    if (!ok) {
      okSince = null
      return false
    }
    okSince ??= now
    return now - okSince >= HOLD_MS
  }

  return { check: () => (detector ? native() : fallback()) }
}
